import { useEffect, useState } from "react";
import { ExternalLink, MoreHorizontal, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STORAGE_KEY = "addin-install-hint-dismissed";
const APPSOURCE_URL =
  "https://marketplace.microsoft.com/en-us/product/WA200010021?tab=Overview"; // email2contact listing

export function AddinInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
  }, []);

  if (!visible) return null;

  const dismissPermanent = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  return (
    <div
      className="
        relative overflow-hidden rounded-2xl border border-primary/30
        bg-gradient-to-br from-primary/10 via-primary/5 to-transparent
        shadow-fluent-1
      "
      role="region"
      aria-label="Install the Outlook Add-In"
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl"
      />

      <div className="relative flex items-start gap-4 p-4 sm:p-5">
        {/* Icon */}
        <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-fluent-2">
          <Sparkles className="h-5 w-5" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm sm:text-base leading-tight">
              Install the Outlook Add-In
            </h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
              Recommended
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Turn any email into a contact directly inside Outlook — Desktop, Web, or Mac.
          </p>

          {/* Actions */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" className="h-8">
              <a
                href={APPSOURCE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Get on AppSource
              </a>
            </Button>
            <span className="text-xs text-muted-foreground">
              or open Outlook → <span className="font-medium text-foreground">Get Add-ins</span> → search{" "}
              <span className="font-medium text-foreground">email2contact</span>
            </span>
          </div>
        </div>

        {/* Dismiss controls */}
        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Banner options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setVisible(false)}>
                Hide for now
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={dismissPermanent}>
                Don't show this again
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => setVisible(false)}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
